import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyWeights,
} from '@carboledger/ui';
import { HelpCircle, X, ArrowRight } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { assistantListPath } from '../constants/chatbotNavigation';
import { useAskAssistantMutation } from '../api/chatbotApi';
import styles from './ChatbotWidget.module.scss';

const MAX_QUESTION_LENGTH = 300;

// A missing figure reads as a dash, never 0 — mass balance distinguishes them.
const formatValue = (value) => (value == null ? '—' : Number(value).toLocaleString('en-US'));

// One figure's value cell — a quantity leg, a list of unit totals, or a count.
const FigureValue = ({ figure }) => {
  if (Array.isArray(figure.totals)) {
    return (
      <>
        {figure.totals.map((total) => (
          <Typography key={total.unit} variant={TypographyVariants.bodySm} as="div">
            {formatValue(total.value)} {total.unit}
          </Typography>
        ))}
      </>
    );
  }
  if (figure.count != null) {
    return <Typography variant={TypographyVariants.bodySm}>{figure.count}</Typography>;
  }
  return (
    <Typography variant={TypographyVariants.bodySm}>
      {formatValue(figure.declarationValue)} {figure.unit}
    </Typography>
  );
};

// A label → value row for one figure.
const FigureRow = ({ figure }) => (
  <div className={styles.figureRow}>
    <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
      {figure.label}
    </Typography>
    <div className={styles.figureValue}>
      <FigureValue figure={figure} />
    </div>
  </div>
);

// The structured payload — either flat metrics or one block per group value.
const DataView = ({ data }) => {
  if (!data) return null;

  if (Array.isArray(data.groups)) {
    return (
      <div className={styles.data}>
        {data.groups.map((group) => (
          <div key={`${group.group.type}-${group.group.name}`} className={styles.group}>
            <Typography variant={TypographyVariants.caption} weight={TypographyWeights.semibold}>
              {group.group.name}
            </Typography>
            {group.metrics.map((figure) => (
              <FigureRow key={figure.key} figure={figure} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(data.metrics)) {
    return (
      <div className={styles.data}>
        {data.metrics.map((figure) => (
          <FigureRow key={figure.key} figure={figure} />
        ))}
      </div>
    );
  }

  return null;
};

// What the bot understood, so a misread is catchable at a glance.
const ScopeCaption = ({ interpretation }) => {
  const bits = [];
  if (interpretation?.entity?.name) bits.push(interpretation.entity.name);
  if (interpretation?.timeRange?.label) bits.push(interpretation.timeRange.label);
  if (!bits.length) return null;
  return (
    <Typography variant={TypographyVariants.caption} color={TypographyColors.secondary} as="div">
      {bits.join(' · ')}
    </Typography>
  );
};

/**
 * Props:
 *   module  the certificate route slug (iscc-eu/iscc-plus/iscc-corsia) the assistant is scoped to
 */
export const ChatbotWidget = ({ module }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [ask, { isLoading }] = useAskAssistantMutation();
  const listRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  const send = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;
      setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
      setQuestion('');
      scrollToEnd();

      const result = await ask({ module, question: trimmed });
      const payload = result?.data;
      const botMessage = payload
        ? {
            role: 'bot',
            text: payload.answer,
            interpretation: payload.interpretation,
            data: payload.data,
            navigation: payload.navigation,
          }
        : { role: 'bot', text: t('CHATBOT_ERROR') };
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    },
    [ask, module, isLoading, scrollToEnd, t],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      send(question);
    },
    [send, question],
  );

  const handleQuestionChange = useCallback((e) => setQuestion(e.target.value), []);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleViewInList = useCallback(
    (navigation) => {
      const path = assistantListPath(navigation, module);
      if (!path) return;
      navigate(path, { state: { assistantFilters: navigation.filters } });
      setOpen(false);
    },
    [navigate, module],
  );

  const suggestions = useMemo(
    () => [
      t('CHATBOT_SUGGESTION_CONTRACTED'),
      t('CHATBOT_SUGGESTION_SHIPPED'),
      t('CHATBOT_SUGGESTION_IN_STOCK'),
    ],
    [t],
  );

  if (!open) {
    return (
      <button
        type="button"
        className={styles.fab}
        aria-label={t('CHATBOT_OPEN')}
        onClick={handleOpen}
      >
        <HelpCircle aria-hidden size={18} />
        <Typography variant={TypographyVariants.bodySm} color={TypographyColors.onBrand}>
          {t('CHATBOT_OPEN')}
        </Typography>
      </button>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Typography
          variant={TypographyVariants.body}
          weight={TypographyWeights.semibold}
          color={TypographyColors.onBrand}
        >
          {t('CHATBOT_TITLE')}
        </Typography>
        <button
          type="button"
          className={styles.close}
          aria-label={t('CHATBOT_CLOSE')}
          onClick={handleClose}
        >
          <X aria-hidden size={18} />
        </button>
      </div>

      <div className={styles.messages} ref={listRef}>
        {messages.length === 0 && (
          <div className={styles.empty}>
            <Typography
              variant={TypographyVariants.bodySm}
              color={TypographyColors.secondary}
              as="div"
            >
              {t('CHATBOT_EMPTY')}
            </Typography>
            <div className={styles.suggestions}>
              {suggestions.map((suggestion) => (
                <Typography
                  key={suggestion}
                  variant={TypographyVariants.bodySm}
                  color={TypographyColors.secondary}
                  as="div"
                >
                  {suggestion}
                </Typography>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={message.role === 'user' ? styles.userMsg : styles.botMsg}
          >
            {message.interpretation && <ScopeCaption interpretation={message.interpretation} />}
            <Typography variant={TypographyVariants.bodySm} as="div">
              {message.text}
            </Typography>
            <DataView data={message.data} />
            {message.navigation && (
              <Button
                variant={ButtonVariants.tertiary}
                size={ButtonSizes.sm}
                rightIcon={<ArrowRight aria-hidden size={14} />}
                onClick={() => handleViewInList(message.navigation)}
              >
                {t('CHATBOT_VIEW_IN_LIST')}
              </Button>
            )}
          </div>
        ))}

        {isLoading && (
          <div className={styles.botMsg}>
            <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
              {t('CHATBOT_THINKING')}
            </Typography>
          </div>
        )}
      </div>

      <form className={styles.inputRow} onSubmit={handleSubmit}>
        <input
          className={styles.input}
          value={question}
          onChange={handleQuestionChange}
          placeholder={t('CHATBOT_PLACEHOLDER')}
          maxLength={MAX_QUESTION_LENGTH}
        />
        <Button
          type="submit"
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          disabled={isLoading}
        >
          {t('CHATBOT_SEND')}
        </Button>
      </form>
    </div>
  );
};
